import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { track, useTracking } from 'react-tracking';
import { useDispatch } from 'react-redux';

import {
  ActionButton,
  ActionSource,
  Appearance,
  Folder,
  Message,
  MessagePublishedStatus,
  MessageType,
  ModeratedBy,
  ViewType
} from '../state/types';
import PaginationBox from '../../../common/pagination-box/pagination-box';
import ModalManager from '../modals/modal-manager';
import { ModalType } from '../modals/state/types';
import FluxibleContext from '../../../fluxible-context';
import { IDashboardProps } from '..';
import { IArticleItem } from '../../articles/types/articleItem';
import { usePrevious } from '../../../../hooks/usePrevious';
import { setPageLoading } from '../state/actions';
import { selectArticlesList } from '../../articles/reducers/articlesSelectors';
import { EmptyModerationMode } from '../../../../../moderation/components/EmptyModerationMode';
import { getHasMorePending, setHasMorePending } from '../state/actions/commentsActions';

import DashboardHeader from './header';
import DashboardList from './list';
import AllFrom from './header/all-from';
import ModerationActiveBar from './moderation-active-bar';
import EmptyList from './list/empty-list';
import { Spinner } from './components/Spinner';
import ModerationMode from './moderation-mode';
import { filterLockedItems, sortLockedItems } from './dashboardUtils';

import { navigateInModeration } from 'app/scripts/components/moderation-v2/comments-and-posts/state/actions/navigate-in-moderation';
import { useTypedSelector } from 'state/index';

function getActionButtonNames(folder: Folder): ActionButton[] {
  let buttons = [];

  switch (folder) {
    case Folder.APPROVED:
      buttons = ['reject'];
      break;
    case Folder.PENDING:
      buttons = ['approve', 'reject'];
      break;
    case Folder.BLOCKED:
      buttons = ['approve'];
      break;
    case Folder.ALL:
      break;
  }

  return buttons;
}

const Dashboard: React.FC<IDashboardProps> = ({
  currentRoute,
  currentUser,
  spotId,
  spotLanguage,
  contentType,
  users,
  posts,
  searchValue,
  searchType,
  itemsList,
  isLoading,
  activeItems,
  expirationTime,
  total,
  isModerationMode,
  pageInfo,
  counts,
  filters,
  lastFetch,
  itemsOnPage,
  isMultiSelect,
  isEmptyResults,
  isSpotItemsFetched,
  totalItems,
  hotKeysSettings,
  fetchSpotItems,
  changePageInfo,
  selectAllItems,
  clearAllItems,
  toggleItem,
  resetAllFilters,
  resetModerationFilters,
  approveItems,
  rejectItems,
  setClientAppearance,
  search,
  changePageFilters,
  toggleModerationActiveBar,
  closeModerationActiveBar,
  openModal,
  spotPolicy,
  setConversationPolicy,
  isMultipleModeratorsMode,
  toggleModerationMode,
  setHighlightTag,
  inpageSkin,
  isHighlightTagsEnabled,
  isModerationViewer
}) => {
  const { executeAction } = useContext(FluxibleContext);
  const [isStickyHeader, setIsStickyHeader] = useState<boolean>(false);
  const stickyHeaderRef = useRef<HTMLDivElement>();
  const { viewType, folder, hasNext } = pageInfo;
  const tracking = useTracking();
  const articles = useTypedSelector(selectArticlesList);
  const sessionModeratedMessageCount = useTypedSelector(
    state => state.moderation.messages.sessionModeratedMessageCount
  );
  const isModerationModePrev = usePrevious(isModerationMode);
  const showEmptyModerationMode = isModerationMode && activeItems === 0;

  const dispatch = useDispatch();

  const { conversation: conversationId } = currentRoute?.query;

  const article = useMemo((): IArticleItem => {
    return articles?.find(article => article.conversationId === conversationId);
  }, []);

  const checkedItems: Message[] = useMemo(
    () => itemsList.filter((item: Message) => item.isChecked()),
    [itemsList]
  );

  const numberOfCheckedItems: number = checkedItems.length;
  const isListVisible: boolean = !isEmptyResults && itemsList.length > 0 && activeItems > 0;
  const actionButtonNames: ActionButton[] = getActionButtonNames(folder);

  const isPendingTab = folder === Folder.PENDING;

  const handleScroll = () => {
    const stickyHeaderNode = stickyHeaderRef?.current;
    const topOffset = stickyHeaderNode?.getBoundingClientRect();
    if (topOffset) {
      setIsStickyHeader(topOffset.top <= 0);
    }
  };

  const handlePagination = useCallback(
    (nextPage: number, nextOffset: number) => {
      if (hasNext) {
        changePageInfo({ offset: nextOffset });
      }
    },
    [hasNext, changePageInfo]
  );

  const canUseActiveBar = (): boolean =>
    folder === Folder.PENDING &&
    itemsList.length > 0 &&
    activeItems > 0 &&
    (isMultiSelect || hotKeysSettings?.isModerationBarActive || isModerationMode);

  const handleMessageActionClick = (type: ActionButton, mode: ActionSource) => {
    const fromKeyboard = mode === 'keyboard';
    const count = checkedItems.length;

    if (count > 0) {
      tracking.trackEvent({
        eventName: `bulk message ${type}`,
        message_count: count,
        keyboard: fromKeyboard ? 'yes' : 'no'
      });
      switch (type) {
        case 'approve':
          approveItems(checkedItems, {
            appearance: fromKeyboard ? Appearance.APPROVED : Appearance.COLLAPSED,
            fromKeyboard
          });
          break;
        case 'reject':
          rejectItems(checkedItems, {
            appearance: fromKeyboard ? Appearance.BLOCKED : Appearance.COLLAPSED,
            fromKeyboard
          });
          break;
        default:
          break;
      }
    }
  };

  const openConfirmationModal = (type: ActionButton, mode: ActionSource) => {
    openModal(ModalType.CONFIRM, {
      onConfirm: () => handleMessageActionClick(type, mode),
      title: 'Confirm Action',
      buttonName: type,
      description: `Are you sure you want to ${type} ${numberOfCheckedItems} comments?`
    });
  };

  const updateDashboardSettingsOnMount = useCallback(() => {
    const { query, params } = currentRoute;
    const { folder, tag, pageNum } = params || {};
    const {
      user,
      ip,
      conversation,
      search,
      searchBy,
      text,
      messageType,
      sortBy,
      published,
      moderatedBy,
      message_id
    } = query || {};
    const tags = tag ? tag.split(',') : ['all'];
    const initialPageInfo = Object.assign({}, pageInfo, {
      folder,
      tag: tags,
      messageType: messageType || MessageType.ALL,
      published: published || MessagePublishedStatus.ALL,
      offset: pageNum ? (pageNum - 1) * itemsOnPage : 0,
      sortBy,
      moderatedBy: moderatedBy || ModeratedBy.ALL
    });

    const isAdvancedFiltersOnMount = Object.keys(query).length > 0;
    changePageInfo(initialPageInfo, true);

    if (isAdvancedFiltersOnMount) {
      const initialFilters = Object.assign({}, filters, { user, ip, conversation, text, message_id });
      const initialSearchSettings = Object.assign(
        {},
        { searchValue, searchType },
        { searchValue: search, searchType: searchBy }
      );
      changePageFilters({
        filters: initialFilters,
        search: initialSearchSettings,
        preventOffsetReset: true
      });
    }
  }, [currentRoute]);

  const fetchItemsAndUpdateURL = () => {
    const withReleaseLock = !isModerationMode && isModerationModePrev;

    executeAction(navigateInModeration, {
      pageInfo,
      itemsOnPage,
      type: contentType,
      filters,
      searchValue,
      searchType
    });
    fetchSpotItems(contentType, withReleaseLock);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    // On mount, update the store's pageInfo and filters settings
    // according to the URL
    updateDashboardSettingsOnMount();

    return () => {
      resetModerationFilters();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const prevIsModerationMode = usePrevious(isModerationMode);

  // When page is loading, fetch more items and update the URL
  useEffect(() => {
    /**
     * When we need refetch items:
     * 1. When on pending tab and moderation mode is turned on we need refetch with a lock
     * 2. When on pending tab and moderation mode is turned off we need refetch without a lock
     * 3. When moderation mode in on and tab was changed to other than pending
     * 4. When moderation mode is off and tab was changed to any tab
     *
     * When we don't need refetch items:
     * 1. When on pending tab and moderation mode is turned on and active items is more than 0
     * 2. When on tab other than pending and moderation mode is turned off
     * 3. When switch to pending tab and moderation mode is on
     */
    const isModerationModeChanged = prevIsModerationMode !== isModerationMode;

    const isModerationModeOnPending =
      !isModerationModeChanged && // if moderation mode was changed we need to refetch with a lock
      isModerationMode &&
      isPendingTab;

    const isModerationModeOffNotPending =
      isModerationModeChanged && !isModerationMode && !isPendingTab && !searchValue;

    const ignoreFetch = isModerationModeOnPending || isModerationModeOffNotPending;

    // isLoading indicates the dashboard is waiting for new fetch
    if (isLoading) {
      if (ignoreFetch) {
        if (activeItems !== 0) {
          // if we don't need to fetch we need to reset the loading state
          dispatch(setPageLoading(false));
        }
      } else {
        fetchItemsAndUpdateURL();
      }
    }
  }, [isLoading, isModerationMode, isPendingTab, activeItems]);

  // When switching between posts and comments tabs
  // we're resetting all the page filters, to load initial state.

  // On spotId change we're fetching new items by resetting all filters
  // TODO: when spotId change will be an action, we'll do it through the reducer.
  useEffect(() => {
    // Do it only after the first fetching has been made
    if (isSpotItemsFetched) {
      fetchItemsAndUpdateURL();
    }
  }, [spotId]);

  // Clicking on COMMENTS or MODERATION nav links changes the url,
  useEffect(() => {
    const { folder, tag, pageNum } = currentRoute.params;
    const isDefaultPageInfo =
      folder === Folder.PENDING &&
      tag === 'all' &&
      !pageNum &&
      Object.keys(currentRoute.query).length === 0;

    if (isDefaultPageInfo) {
      fetchItemsAndUpdateURL();
    }
  }, [currentRoute]);

  useEffect(() => {
    if (activeItems === 0 && isModerationMode && isPendingTab && sessionModeratedMessageCount > 0) {
      clearAllItems();
      dispatch(getHasMorePending({ spotId, pageInfo }));
    }
  }, [
    activeItems,
    isModerationMode,
    isPendingTab,
    clearAllItems,
    spotId,
    dispatch,
    sessionModeratedMessageCount,
    pageInfo
  ]);

  const onStartNewSession = () => {
    dispatch(setHasMorePending(false));
    fetchItemsAndUpdateURL();
  };

  const finalItems = useMemo(() => {
    if (isModerationMode && isPendingTab) {
      const sorted = sortLockedItems(itemsList, pageInfo);
      return filterLockedItems(sorted, pageInfo);
    } else {
      return itemsList;
    }
  }, [isModerationMode, pageInfo, itemsList, isPendingTab]);

  return (
    <div className="dashboard">
      {isMultipleModeratorsMode && !isModerationViewer && (
        <ModerationMode toggleModerationMode={toggleModerationMode} openModal={openModal} />
      )}
      {viewType && viewType !== ViewType.SPOT && viewType !== ViewType.TEXT && (
        <AllFrom
          conversation={posts?.[filters?.conversation]}
          user={users?.[filters?.user]}
          ip={filters?.ip}
          pageInfo={pageInfo}
          resetAllFilters={resetAllFilters}
          isClearFilterButtonDisabled={isLoading}
        />
      )}

      <div ref={stickyHeaderRef}>
        <DashboardHeader
          article={article}
          pageInfo={pageInfo}
          counts={counts}
          changePageInfo={changePageInfo}
          resetAllFilters={resetAllFilters}
          isMultiSelect={isMultiSelect}
          onOpenModal={openConfirmationModal}
          numberOfCheckedItems={numberOfCheckedItems}
          buttons={actionButtonNames}
          numberOfItems={activeItems}
          selectAllItems={selectAllItems}
          clearAllItems={clearAllItems}
          searchType={searchType}
          searchValue={searchValue}
          search={search}
          isStickyHeader={isStickyHeader}
          contentType={contentType}
        />
      </div>

      {searchValue && !isEmptyResults && (
        <h5 className={'search-results-text'}> {`Search Results for "${searchValue}"`} </h5>
      )}
      {isLoading && <Spinner gapSize={'normal'} size={'normal'} />}
      {isListVisible && !showEmptyModerationMode && (
        <>
          <DashboardList
            itemsList={finalItems}
            pageInfo={pageInfo}
            isMultiSelectVisble={isMultiSelect}
            isModerationMode={isModerationMode}
            searchValue={searchValue}
            canUseActiveBar={canUseActiveBar}
            openConfirmationModal={openConfirmationModal}
            users={users}
            posts={posts}
            inpageSkin={inpageSkin}
            toggleItem={toggleItem}
            clearAllItems={clearAllItems}
            approveItems={approveItems}
            rejectItems={rejectItems}
            setClientAppearance={setClientAppearance}
            filters={filters}
            changePageFilters={changePageFilters}
            hotKeysSettings={hotKeysSettings}
            openModal={openModal}
            spotPolicy={spotPolicy}
            setConversationPolicy={setConversationPolicy}
            isMultipleModeratorsMode={isMultipleModeratorsMode}
            contentType={contentType}
            setHighlightTag={setHighlightTag}
            spotId={spotId}
            isHighlightTagsEnabled={isHighlightTagsEnabled}
            currentUser={currentUser}
            isModerationViewer={isModerationViewer}
          />
          {!(isModerationMode && isPendingTab) && (
            <PaginationBox
              itemsPerPage={itemsOnPage}
              totalRecords={totalItems}
              offset={pageInfo.offset}
              onPageChanged={handlePagination}
            />
          )}
        </>
      )}
      {!isListVisible && !showEmptyModerationMode && (
        <EmptyList
          isLoading={isLoading}
          counts={counts}
          folder={folder}
          activeItems={activeItems}
          itemsList={itemsList}
          isModerationMode={isModerationMode}
          changePageInfo={changePageInfo}
        />
      )}

      {showEmptyModerationMode && !isLoading && <EmptyModerationMode onClick={onStartNewSession} />}

      {canUseActiveBar() && (
        <ModerationActiveBar
          hotKeysSettings={hotKeysSettings}
          onCloseActiveBar={closeModerationActiveBar}
          onToggleActiveBar={toggleModerationActiveBar}
          itemsList={itemsList}
          numberOfCheckedItems={numberOfCheckedItems}
        />
      )}
      <ModalManager />
    </div>
  );
};

export default track(props => {
  const { currentUser } = props;
  return {
    moderation_tab: props.contentType,
    inner_page: props.pageInfo?.folder,
    user_name: currentUser.username,
    display_user_name: currentUser.displayName
  };
})(Dashboard);
export { TAGS } from './tags/moderationTags';
export { ALL } from './tags/moderationTags';
